import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { useIntl } from 'react-intl';

const TITLE_ID = 'discard-changes-title';
const BODY_ID = 'discard-changes-body';

type Props = {
    open: boolean;
    onCancel: () => void;
    /** Discard every local modification and reload from the database. */
    onConfirm: () => void;
};

/**
 * Confirmation for the irreversible discard-and-reload. Shared by the
 * pending-changes panel and the save split button's menu so both paths
 * carry the identical wording and the same keyboard safety: Cancel takes
 * focus, so Enter can never discard.
 */
export default function DiscardChangesDialog({
    open,
    onCancel,
    onConfirm,
}: Props) {
    const intl = useIntl();
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby={TITLE_ID}
            aria-describedby={BODY_ID}
            maxWidth="xs"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle id={TITLE_ID}>
                {intl.formatMessage({ id: 'saveChanges.discardConfirmTitle' })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={BODY_ID}>
                    {intl.formatMessage({
                        id: 'saveChanges.discardConfirmBody',
                    })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {/* Cancel takes focus: Enter must never discard. */}
                <Button onClick={onCancel} color="inherit" autoFocus>
                    {intl.formatMessage({ id: 'saveChanges.cancel' })}
                </Button>
                <Button
                    onClick={onConfirm}
                    color="error"
                    data-testid="discard-confirm-button"
                >
                    {intl.formatMessage({ id: 'saveChanges.discardReload' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
